import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, Cookie, Settings, Eye, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const PoliticaCookies = () => {
  const { siteSettings } = useContext(AuthContext);
  const siteName = siteSettings?.site_name || 'Nossa Feira';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Política de Cookies</h1>
              <p className="text-gray-600">Última atualização: Outubro de 2024</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          
          {/* Introduction */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-yellow-600" />
                1. O que são Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <p>
                Cookies são pequenos arquivos de texto que são colocados no seu dispositivo (computador, smartphone ou tablet) quando you visita nosso site. Eles são amplamente utilizados para fazer os sites funcionarem de forma mais eficiente, bem como para fornecer informações aos proprietários do site.
              </p>
              <p>
                O {siteName} utiliza cookies para melhorar sua experiência de navegação, lembrar suas preferências e fornecer funcionalidades essenciais da plataforma.
              </p>
            </CardContent>
          </Card>

          {/* Types of Cookies */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-600" />
                2. Tipos de Cookies que Utilizamos
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>2.1 Cookies Essenciais</h4>
              <p>
                Estes cookies são necessários para o funcionamento básico do site e não podem ser desativados em nossos sistemas. Geralmente, eles são definidos apenas em resposta a ações feitas por você, como fazer login ou configurar suas preferências de privacidade.
              </p>
              <ul>
                <li>Cookie de autenticação (para manter você logado)</li>
                <li>Cookie de sessão (para navegar entre páginas)</li>
                <li>Cookie de segurança (para proteção contra ataques)</li>
              </ul>

              <h4>2.2 Cookies de Performance</h4>
              <p>
                Estes cookies nos permitem contar visitas e fontes de tráfego para que possamos medir e melhorar o performance do nosso site. Eles nos ajudam a saber quais páginas são mais e menos populares e ver como os visitantes se movem pelo site.
              </p>
              <ul>
                <li>Análise de uso da plataforma</li>
                <li>Tempo de permanência nas páginas</li>
                <li>Caminhos de navegação mais utilizados</li>
              </ul>

              <h4>2.3 Cookies de Funcionalidade</h4>
              <p>
                Estes cookies permitem que o site forneça funcionalidade e personalização aprimoradas. Podem ser definidos por nós ou por fornecedores terceiros cujos serviços adicionamos às nossas páginas.
              </p>
              <ul>
                <li>Preferências de filtros no marketplace</li>
                <li>Histórico de produtos visualizados</li>
                <li>Configurações de interface do usuário</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cookie Management */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-600" />
                3. Gerenciamento de Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>3.1 Controle através do Navegador</h4>
              <p>
                Você pode controlar e/ou excluir cookies como desejar. Você pode excluir todos os cookies que já estão no seu computador e pode configurar a maioria dos navegadores para impedir que sejam colocados.
              </p>

              <h4>3.2 Como Desativar Cookies</h4>
              <p><strong>Google Chrome:</strong></p>
              <ol>
                <li>Clique no menu (três pontos) > Configurações</li>
                <li>Clique em "Privacidade e segurança" > "Cookies e outros dados do site"</li>
                <li>Escolha "Bloquear cookies de terceiros" ou "Bloquear todos os cookies"</li>
              </ol>

              <p><strong>Mozilla Firefox:</strong></p>
              <ol>
                <li>Clique no menu > Opções</li>
                <li>Selecione "Privacidade e Segurança"</li>
                <li>Em "Cookies e Dados do Site", escolha suas preferências</li>
              </ol>

              <p><strong>Safari:</strong></p>
              <ol>
                <li>Vá para Safari > Preferências</li>
                <li>Clique na aba "Privacidade"</li>
                <li>Escolha suas configurações de cookies</li>
              </ol>
            </CardContent>
          </Card>

          {/* Third Party Cookies */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" />
                4. Cookies de Terceiros
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>4.1 WhatsApp</h4>
              <p>
                Quando você clica para contatar um produtor via WhatsApp, você será redirecionado para o WhatsApp Web ou aplicativo. O WhatsApp pode definir seus próprios cookies, que estão sujeitos à política de privacidade do WhatsApp.
              </p>

              <h4>4.2 Não Utilizamos</h4>
              <p>
                O {siteName} não utiliza cookies de publicidade de terceiros, cookies de redes sociais para rastreamento, ou cookies de análise de terceiros como Google Analytics.
              </p>
            </CardContent>
          </Card>

          {/* Cookie Retention */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>5. Tempo de Armazenamento</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>5.1 Cookies de Sessão</h4>
              <p>
                São excluídos automaticamente quando você fecha o navegador. Utilizamos para manter você logado durante sua visita.
              </p>

              <h4>5.2 Cookies Persistentes</h4>
              <p>
                Permanecem no seu dispositivo por um período determinado ou até que você os exclua. Utilizamos para lembrar suas preferências entre visitas.
              </p>
              <ul>
                <li>Cookie de login: até 30 dias</li>
                <li>Cookie de preferências: até 1 ano</li>
                <li>Cookie de configurações: até 6 meses</li>
              </ul>
            </CardContent>
          </Card>

          {/* Changes and Contact */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>6. Alterações e Contato</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>6.1 Atualizações desta Política</h4>
              <p>
                Podemos atualizar esta Política de Cookies ocasionalmente para refletir mudanças em nossas práticas ou por outros motivos operacionais, legais ou regulamentares.
              </p>

              <h4>6.2 Entre em Contato</h4>
              <p>
                Se você tiver dúvidas sobre nossa utilização de cookies, entre em contato:
              </p>
              <ul>
                <li><strong>Email:</strong> {siteSettings?.contact_email || 'contato@nossafeira.com'}</li>
                <li><strong>Telefone:</strong> {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 p-6 bg-yellow-50 rounded-lg">
          <p className="text-center text-gray-600 text-sm">
            Para mais informações sobre como protegemos seus dados, consulte nossa{' '}
            <Link to="/politica-de-privacidade" className="text-yellow-600 hover:underline">
              Política de Privacidade
            </Link>
            {' '}e nossos{' '}
            <Link to="/termos-de-uso" className="text-yellow-600 hover:underline">
              Termos de Uso
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default PoliticaCookies;